import React, { useState } from 'react';
import { CheckCircle, Loader, AlertCircle } from 'lucide-react';

interface Props { currentUser?: any; }

const VILLAGES = [
  { id:'v-1',  name:'Jattu-Uzairue',       icon:'👑', heritage:'Royal seat, palace, administrative capital' },
  { id:'v-2',  name:'Afashio-Uzairue',      icon:'🛡️', heritage:'Egbele age-grade, craftsmanship' },
  { id:'v-3',  name:'Afowa-Uzairue',        icon:'🌳', heritage:'Founding settlement, iroko grove traditions' },
  { id:'v-4',  name:'Apana-Uzairue',        icon:'🌊', heritage:'River heritage, fishing rites' },
  { id:'v-5',  name:'Ayaoghena-Uzairue',    icon:'🎭', heritage:'Ancestral pathway, memorial masquerades' },
  { id:'v-6',  name:'Ayogwiri-Uzairue',     icon:'⚔️', heritage:'Warrior heritage, traditional wrestling' },
  { id:'v-7',  name:'Ayua-Uzairue',         icon:'🌿', heritage:'Spiritual authority, traditional medicine' },
  { id:'v-8',  name:'Elele-Uzairue',        icon:'🌾', heritage:'Agricultural sanctuary, New Yam & Iko Ebo' },
  { id:'v-9',  name:'Idatto-Uzairue',       icon:'🏺', heritage:'Pottery, women-led clay artisanship' },
  { id:'v-10', name:'Ikabigbo-Uzairue',     icon:'🥊', heritage:'Traditional wrestling, wood carving' },
  { id:'v-11', name:'Ikholo-Uzairue',       icon:'⚖️', heritage:'Mediation, elder council, peacemaking' },
  { id:'v-12', name:'Imeke-Uzairue',        icon:'🧵', heritage:'Master weaving, ceremonial textiles' },
  { id:'v-13', name:'Imhonikhe-Uzairue',    icon:'🏛️', heritage:'Ancestral shrine custodians' },
  { id:'v-14', name:'Irekpai-Uzairue',      icon:'⚒️', heritage:'Blacksmithing, iron working' },
  { id:'v-15', name:'Iyamho-Uzairue',       icon:'📚', heritage:'Education, AAU corridor' },
  { id:'v-16', name:'Iyora-Uzairue',        icon:'🎪', heritage:'Ekaba Festival, sacred masquerade' },
  { id:'v-17', name:'Iyuku-Uzairue',        icon:'🏪', heritage:'Market and trade heritage' },
  { id:'v-18', name:'Ogbido-Uzairue',       icon:'🌿', heritage:'Pottery, herbal medicine' },
  { id:'v-19', name:'Ozor-Uzairue',         icon:'🌲', heritage:"Hunters' Guild, forest knowledge" },
  { id:'v-20', name:'Ugbenor-Uzairue',      icon:'🌴', heritage:'Palm oil, women-led cooperatives' },
  { id:'v-21', name:'Uluoke-Uzairue',       icon:'⛰️', heritage:'Elevated terrain, sentinel heritage' },
  { id:'v-22', name:'Yelwa-Uzairue',        icon:'💰', heritage:'Commercial prosperity' },
];

type StatusMap = Record<string,'idle'|'creating'|'done'|'error'>;

const CommunityQuickSetup: React.FC<Props> = ({ currentUser }) => {
  const [statuses, setStatuses]   = useState<StatusMap>({});
  const [bulkRunning, setBulkRunning] = useState(false);
  const [summary, setSummary]     = useState('');

  const isAdmin = currentUser?.email === 'erueuzairue@gmail.com' ||
                  currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const initPortal = async (id: string, name: string, heritage: string) => {
    setStatuses(s => ({...s, [id]: 'creating'}));
    try {
      const r = await fetch(`/api/community-portals/${id}`, {
        method: 'PUT',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          published: false, villageId: id, villageName: name,
          history: `${name} is one of the 22 autonomous communities of Uzairue Kingdom, Etsako West LGA, Edo State, Nigeria. Heritage: ${heritage}`,
          resources: [], gallery: [], team: [], posts: [], announcements: [], timeline: [],
          heroImageUrl: '', chiefName: '', chiefTitle: '', chiefImageUrl: '', chiefMessage: '',
          contactEmail: '', contactPhone: '',
          contactAddress: `${name}, Etsako West LGA, Edo State, Nigeria`,
        }),
      });
      const d = await r.json();
      setStatuses(s => ({...s, [id]: d.success ? 'done' : 'error'}));
    } catch {
      setStatuses(s => ({...s, [id]: 'error'}));
    }
  };

  const runAll = async () => {
    setBulkRunning(true); setSummary('');
    for (let i = 0; i < VILLAGES.length; i++) {
      const v = VILLAGES[i];
      await initPortal(v.id, v.name, v.heritage);
      setSummary(`Initialised ${i+1}/22 portals…`);
      await new Promise(r => setTimeout(r, 250));
    }
    setBulkRunning(false);
    setSummary('✅ All 22 community portals initialised successfully.');
  };

  if (!isAdmin) return (
    <div className="text-center py-16 text-[#6B8070] text-sm">Admin access required.</div>
  );

  const statusIcon = (id: string) => {
    const s = statuses[id];
    if (s === 'creating') return <Loader className="w-4 h-4 text-[#C9A84C] animate-spin" />;
    if (s === 'done')     return <CheckCircle className="w-4 h-4 text-green-400" />;
    if (s === 'error')    return <AlertCircle className="w-4 h-4 text-red-400" />;
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="royal-card p-6">
        <h2 className="text-lg font-bold text-[#EDE8DC] mb-2" style={{fontFamily:"'Cinzel',serif"}}>
          Community Portal Quick Setup
        </h2>
        <p className="text-[#6B8070] text-sm mb-4">
          Initialise all 22 community portals with default templates.
          Each portal can then be individually customised.
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={runAll} disabled={bulkRunning}
            className="btn-gold flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-50">
            {bulkRunning
              ? <><Loader className="w-4 h-4 animate-spin" /> Setting up…</>
              : '🚀 Initialise All 22 Portals'}
          </button>
          {summary && <p className="text-sm text-[#C9A84C]">{summary}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {VILLAGES.map(v => (
          <div key={v.id} className="royal-card p-4 flex items-center gap-3">
            <span className="text-2xl flex-shrink-0">{v.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[#EDE8DC] font-medium text-sm truncate">{v.name}</p>
              <p className="text-[#6B8070] text-xs">
                {statuses[v.id] === 'done'     ? '✅ Initialised' :
                 statuses[v.id] === 'creating' ? '⏳ Setting up…' :
                 statuses[v.id] === 'error'    ? '❌ Error' : '○ Pending'}
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              {statusIcon(v.id)}
              {!statuses[v.id] && (
                <button onClick={() => initPortal(v.id, v.name, v.heritage)}
                  className="text-xs text-[#C9A84C] hover:underline">Init</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommunityQuickSetup;
